import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Badge,
} from "@mui/material";
import { useSelector } from "react-redux";
import FilterListIcon from "@mui/icons-material/FilterList";
// import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import OverviewDashboard from "./OverviewDashboard";
import PerformanceDashboard from "./PerformanceDashboard";
import TalkWithDataModal from "../../ContractDashboard/TalkWithDataModal";
import FilterModal, { FilterValues } from "../FilterModal";
import { RootState, useAppDispatch } from "../../../store";
import {
  fetchAllContractData,
  clearContractsError,
  setContractsFilters,
} from "../../../store/procurementDashboardSlice";
import overviewIcon from "../../../assets/images/overviewImg.svg";
import performanceIcon from "../../../assets/images/performanceImg.svg";
import styles from "./contractsSection.module.scss";

const ContractsSection = () => {
  const dispatch = useAppDispatch();
  const [dashboardTab, setDashboardTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Select data from Redux store
  const {
    contractOverview,
    contractAnalytics,
    contractsLoading,
    contractsError,
    contractsFilters,
  } = useSelector((state: RootState) => state.procurementDashboard);

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearContractsError());

    // Fetch data only if not already loaded
    if (!contractOverview || !contractAnalytics) {
      dispatch(fetchAllContractData(contractsFilters));
    }
  }, [dispatch]);

  const handleDashboardChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setDashboardTab(newValue);
  };

  const handleRefresh = () => {
    dispatch(fetchAllContractData(contractsFilters));
  };

  const handleApplyFilters = (newFilters: FilterValues) => {
    dispatch(setContractsFilters(newFilters));
    dispatch(fetchAllContractData(newFilters));
  };

  const activeFilterCount =
    (contractsFilters.group_company && contractsFilters.group_company.length > 0
      ? 1
      : 0) +
    (contractsFilters.contract_owner && contractsFilters.contract_owner.length > 0
      ? 1
      : 0) +
    (contractsFilters.start_date_with_sd || contractsFilters.end_date_with_sd ? 1 : 0) +
    (contractsFilters.start_date_with_ed || contractsFilters.end_date_with_ed ? 1 : 0) +
    (contractsFilters.start_date_with_cct_created_at || contractsFilters.end_date_with_cct_created_at ? 1 : 0) +
    (contractsFilters.start_date_with_cct_updated_at || contractsFilters.end_date_with_cct_updated_at ? 1 : 0);

  const renderDashboardContent = () => {
    if (contractsLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (contractsError) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="body1" color="error">
            {contractsError}
          </Typography>
        </Box>
      );
    }

    if (!contractOverview || !contractAnalytics) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      );
    }

    switch (dashboardTab) {
      case 0:
        return (
          <OverviewDashboard
            overviewData={contractOverview}
            analyticsData={contractAnalytics}
          />
        );
      case 1:
        return (
          <PerformanceDashboard
            analyticsData={contractAnalytics}
            overviewData={contractOverview}
            filters={contractsFilters}
          />
        );
      default:
        return (
          <OverviewDashboard
            overviewData={contractOverview}
            analyticsData={contractAnalytics}
          />
        );
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h4" className={styles.title}>
            Contracts Management
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Comprehensive contract tracking and analytics
          </Typography>
        </Box>
        <Box className={styles.headerActions}>
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              className={styles.actionButton}
              onClick={() => setFilterModalOpen(true)}
            >
              Filters
            </Button>
          </Badge>
          {/* <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            className={styles.actionButton}
          >
            Export
          </Button> */}
          <Button
            variant="outlined"
            startIcon={<ChatBubbleOutlineIcon />}
            className={styles.actionButton}
            onClick={() => setModalOpen(true)}
          >
            Talk with Data
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={contractsLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box className={styles.dashboardTabs}>
        <Tabs
          value={dashboardTab}
          onChange={handleDashboardChange}
          sx={{ minHeight: "48px" }}
          TabIndicatorProps={{
            style: { backgroundColor: "#00d2aa" },
          }}
        >
          <Tab
            icon={
              <img
                src={overviewIcon}
                alt="Overview Dashboard"
                style={{
                  width: 18,
                  height: 18,
                  marginRight: 6,
                  filter:
                    dashboardTab === 0
                      ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                      : "grayscale(100%) opacity(1)",
                }}
              />
            }
            label="Overview Dashboard"
            iconPosition="start"
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />

          <Tab
            icon={
              <img
                src={performanceIcon}
                alt="Performance Dashboard"
                style={{
                  width: 18,
                  height: 18,
                  marginRight: 6,
                  filter:
                    dashboardTab === 1
                      ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                      : "grayscale(100%) opacity(1)",
                }}
              />
            }
            label="Performance Dashboard"
            iconPosition="start"
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />
        </Tabs>
      </Box>

      {renderDashboardContent()}
      <TalkWithDataModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        section="contracts"
        initialFilters={contractsFilters}
      />
    </Box>
  );
};

export default ContractsSection;
